import { Box, Button, Typography } from "@braintrust/braintrust-ui-components";
import { BID_SUBMIT_TYPE } from "@js/apps/jobs/constants";
import { getRejectOfferMessage } from "@js/services";

import { BidSendMessage } from "../../forms/bid-send-message";
import { useRejectOfferMessageSubmitType } from "../../hooks/use-reject-offer-message-submit-type";
import type { RejectOfferMessageWrapperProps } from "../../models";

export const RejectOfferMessageWrapper = ({
  bids,
  nextUrl,
  refetchBidList,
  job,
}: RejectOfferMessageWrapperProps) => {
  return (
    <RejectOfferMessage
      refetchBidList={refetchBidList}
      nextUrl={nextUrl}
      bids={bids}
      job={job}
    />
  );
};

const RejectOfferMessage = ({
  nextUrl,
  bids,
  refetchBidList,
  job,
}: RejectOfferMessageWrapperProps) => {
  const [bid] = bids;
  const isBulk = bids.length > 1;
  const name = (!isBulk && bid?.freelancer.user.first_name) || "";
  const greeting = name ? `Hi ${name}` : "Hi";
  const initialMessage =
    `${greeting}, \n\nI wanted to let you know we decided to not move forward with ` +
    `your application for ${job.title}. Please don't hesitate to apply to future roles! \n\nThanks`;

  const savedMessage = !isBulk ? getRejectOfferMessage(bid?.id) : "";
  const message = savedMessage || initialMessage;

  const headlineName = isBulk ? `${bids.length} applicants` : name;

  const { updateSubmitType, onSubmit } = useRejectOfferMessageSubmitType({
    bids,
    nextUrl,
    refetchBidList,
  });

  return (
    <Box sx={{ bgcolor: "var(--soft-orange)", p: 3, maxWidth: 576 }}>
      <Typography component="h2" variant="label" sx={{ pr: 3, mb: 1 }}>
        Let {headlineName} know you won’t be hiring them
      </Typography>
      <Typography component="p" size="small" sx={{ mb: 2 }}>
        Talent prefer to get a rejection vs. being left in the dark. This is
        especially true if you’ve moved someone to the interview stage. It helps
        them do better next time ❤️
      </Typography>
      {!isBulk && bid && (
        <BidSendMessage
          form="send-interview-request"
          onSubmit={onSubmit}
          initialValues={{
            message,
          }}
          ButtonProps={{
            size: "x-small",
            children: "Add feedback",
            variant: "medium-orange",
            onClick: () => updateSubmitType(BID_SUBMIT_TYPE.WITH_FEEDBACK),
            type: "submit",
          }}
          additionalButtons={[
            <Button
              key="send-message-without-feedback-button"
              variant="transparent"
              size="x-small"
              shape="squared"
              onClick={() => updateSubmitType(BID_SUBMIT_TYPE.WITHOUT_FEEDBACK)}
              type="submit"
            >
              Send without feedback
            </Button>,
          ]}
          minRows={4}
        />
      )}

      {isBulk && (
        <BidSendMessage
          form="send-interview-request"
          onSubmit={onSubmit}
          initialValues={{
            message,
          }}
          ButtonProps={{
            size: "x-small",
            shape: "squared",
            children: `Send ${bids.length} rejections`,
            variant: "medium-orange",
            onClick: () => updateSubmitType(BID_SUBMIT_TYPE.WITH_FEEDBACK_BULK),
            type: "submit",
          }}
          minRows={4}
          bulkRejection
        />
      )}
    </Box>
  );
};
